import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Collapse,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ValidationRule } from '../../types/questionnaire.types';

interface ValidationEditorProps {
  fieldType?: string;
  validation?: ValidationRule;
  onChange: (validation?: ValidationRule) => void;
  allFields?: Array<{ name: string; label: string }>;  // For matchField dropdown
}

export const ValidationEditor: React.FC<ValidationEditorProps> = ({ fieldType, validation, onChange, allFields }) => {
  const [expanded, setExpanded] = useState(false);
  const [validationTypeOpen, setValidationTypeOpen] = useState(false);
  const [matchFieldOpen, setMatchFieldOpen] = useState(false);
  const [localValidation, setLocalValidation] = useState<ValidationRule>(
    validation || {
      minLength: undefined,
      maxLength: undefined,
      pattern: undefined,
      message: undefined,
      min: undefined,
      max: undefined,
      validationType: undefined,
      matchFieldName: undefined,
      dateWithinDays: undefined,
      minAge: undefined,
    }
  );

  // Update localValidation when the validation prop changes (e.g., when editing a field)
  // Always reset localValidation to ensure no stale data from previous field
  // Depend on primitive values to avoid infinite loops from object reference changes
  useEffect(() => {
    let initialValidation = validation || {
      minLength: undefined,
      maxLength: undefined,
      pattern: undefined,
      message: undefined,
      min: undefined,
      max: undefined,
      validationType: undefined,
      matchFieldName: undefined,
      dateWithinDays: undefined,
      minAge: undefined,
    };

    // Clear irrelevant validation fields based on field type
    if (fieldType === 'number') {
      // For number fields, delete string-related validations entirely
      const { minLength, maxLength, pattern, ...rest } = initialValidation;
      initialValidation = rest;
    } else if (fieldType !== 'date' && fieldType !== 'text') {
      // For non-date and non-text fields, delete custom validation types
      const { validationType, matchFieldName, dateWithinDays, minAge, ...rest } = initialValidation;
      initialValidation = rest;
    } else {
      // For non-number fields, delete number-related validations entirely
      const { min, max, ...rest } = initialValidation;
      initialValidation = rest;
    }

    setLocalValidation(initialValidation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation?.min, validation?.max, validation?.minLength, validation?.maxLength, validation?.pattern, validation?.message, validation?.validationType, validation?.matchFieldName, validation?.dateWithinDays, validation?.minAge, fieldType]);

  const handleChange = (field: keyof ValidationRule, value: any) => {
    const updated = { ...localValidation, [field]: value };
    setLocalValidation(updated);
    // Update parent immediately when validation changes
    // Filter out undefined and null values AND remove field-type-incompatible keys
    let cleanedValidation: ValidationRule = {};
    Object.entries(updated).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        (cleanedValidation as any)[key] = val;
      }
    });

    // Also remove incompatible fields based on fieldType
    if (fieldType === 'number') {
      // For number fields, remove string-specific validations AND custom validation types
      const { minLength, maxLength, pattern, validationType, matchFieldName, dateWithinDays, minAge, ...numberValidation } = cleanedValidation;
      cleanedValidation = numberValidation as ValidationRule;
    } else if (fieldType === 'date') {
      // For date fields, remove string validations (but keep custom validation types)
      const { min, max, minLength, maxLength, ...dateValidation } = cleanedValidation;
      cleanedValidation = dateValidation as ValidationRule;
    } else if (fieldType === 'text') {
      // For text fields, remove number validations (but keep custom validation types)
      const { min, max, ...textValidation } = cleanedValidation;
      cleanedValidation = textValidation as ValidationRule;
    } else if (fieldType) {
      // For other field types, remove ALL custom validations
      const { min, max, minLength, maxLength, pattern, validationType, matchFieldName, dateWithinDays, minAge, ...otherValidation } = cleanedValidation;
      cleanedValidation = otherValidation as ValidationRule;
    }

    const hasAnyValue = Object.keys(cleanedValidation).length > 0;
    onChange(hasAnyValue ? cleanedValidation : undefined);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="body2">Validation Rules (Optional)</Typography>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* String validation fields (for text, textarea, etc. - NOT for date or number) */}
          {fieldType !== 'number' && fieldType !== 'date' && (
            <>
              <TextField
                fullWidth
                size="small"
                label="Min Length"
                type="number"
                value={localValidation.minLength || ''}
                onChange={(e) =>
                  handleChange('minLength', e.target.value ? parseInt(e.target.value) : undefined)
                }
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Max Length"
                type="number"
                value={localValidation.maxLength || ''}
                onChange={(e) =>
                  handleChange('maxLength', e.target.value ? parseInt(e.target.value) : undefined)
                }
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Regex Pattern"
                value={localValidation.pattern || ''}
                onChange={(e) =>
                  handleChange('pattern', e.target.value || undefined)
                }
                placeholder="e.g., ^[A-Z]+$"
                helperText="Optional regex for advanced validation"
              />
            </>
          )}

          {/* Number validation fields (for number field type) */}
          {fieldType === 'number' && (
            <>
              <TextField
                fullWidth
                size="small"
                label="Minimum Value"
                type="number"
                value={localValidation.min !== undefined ? localValidation.min : ''}
                onChange={(e) =>
                  handleChange('min', e.target.value !== '' ? parseFloat(e.target.value) : undefined)
                }
                helperText="Minimum allowed value"
              />
              <TextField
                fullWidth
                size="small"
                label="Maximum Value"
                type="number"
                value={localValidation.max !== undefined ? localValidation.max : ''}
                onChange={(e) =>
                  handleChange('max', e.target.value !== '' ? parseFloat(e.target.value) : undefined)
                }
                helperText="Maximum allowed value"
              />
            </>
          )}

          {/* Custom Validation Types (for date and cross-field validation) */}
          {(fieldType === 'date' || fieldType === 'text') && (
            <>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="validation-type-label">Validation Type</InputLabel>
                <Select
                  labelId="validation-type-label"
                  id="validation-type-select"
                  value={localValidation.validationType || ''}
                  label="Validation Type"
                  open={validationTypeOpen}
                  onOpen={() => setValidationTypeOpen(true)}
                  onClose={() => setValidationTypeOpen(false)}
                  displayEmpty
                  renderValue={(value) => {
                    const labels: Record<string, string> = {
                      pastDate: 'Past Date (must be in the past)',
                      futureDate: 'Future Date (must be in the future)',
                      dateWithinDays: 'Date Within X Days',
                      minAge: 'Minimum Age (calculate from DOB)',
                      matchField: 'Match Another Field',
                    };

                    return labels[value as string] || value;
                  }}
                >
                  {fieldType === 'date' && (
                    <>
                      <MenuItem value="pastDate" onClick={() => { handleChange('validationType', 'pastDate'); setValidationTypeOpen(false); }}>
                        Past Date (must be in the past)
                      </MenuItem>
                      <MenuItem value="futureDate" onClick={() => { handleChange('validationType', 'futureDate'); setValidationTypeOpen(false); }}>
                        Future Date (must be in the future)
                      </MenuItem>
                      <MenuItem value="dateWithinDays" onClick={() => { handleChange('validationType', 'dateWithinDays'); setValidationTypeOpen(false); }}>
                        Date Within X Days
                      </MenuItem>
                      <MenuItem value="minAge" onClick={() => { handleChange('validationType', 'minAge'); setValidationTypeOpen(false); }}>
                        Minimum Age (calculate from DOB)
                      </MenuItem>
                    </>
                  )}
                  {fieldType === 'text' && (
                    <MenuItem value="matchField" onClick={() => { handleChange('validationType', 'matchField'); setValidationTypeOpen(false); }}>
                      Match Another Field
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Conditional fields based on validation type */}
              {localValidation.validationType === 'matchField' && allFields && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="match-field-label">Field to Match</InputLabel>
                  <Select
                    labelId="match-field-label"
                    id="match-field-select"
                    value={localValidation.matchFieldName || ''}
                    label="Field to Match"
                    open={matchFieldOpen}
                    onOpen={() => setMatchFieldOpen(true)}
                    onClose={() => setMatchFieldOpen(false)}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) return <em style={{ color: '#999' }}>Select field...</em>;
                      const matchedField = allFields.find(f => f.name === value);
                      return matchedField ? `${matchedField.label} (${matchedField.name})` : value;
                    }}
                  >
                    {allFields.map((field) => (
                      <MenuItem
                        key={field.name}
                        value={field.name}
                        onClick={() => { handleChange('matchFieldName', field.name); setMatchFieldOpen(false); }}
                      >
                        {field.label} ({field.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {localValidation.validationType === 'dateWithinDays' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Number of Days"
                  type="number"
                  value={localValidation.dateWithinDays || ''}
                  onChange={(e) =>
                    handleChange('dateWithinDays', e.target.value ? parseInt(e.target.value) : undefined)
                  }
                  inputProps={{ min: 1 }}
                  helperText="e.g., 14 for 'within last 14 days'"
                  sx={{ mt: 1 }}
                />
              )}

              {localValidation.validationType === 'minAge' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Minimum Age"
                  type="number"
                  value={localValidation.minAge || ''}
                  onChange={(e) =>
                    handleChange('minAge', e.target.value ? parseInt(e.target.value) : undefined)
                  }
                  inputProps={{ min: 0, max: 150 }}
                  helperText="e.g., 18 for 'must be 18 or older'"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}

          <TextField
            fullWidth
            size="small"
            label="Error Message"
            value={localValidation.message || ''}
            onChange={(e) =>
              handleChange('message', e.target.value || undefined)
            }
            placeholder="Error message to show if validation fails"
            multiline
            rows={2}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
